import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Card,
  CardBody,
  CardHeader,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Spinner,
  Alert,
  AlertIcon,
  useToast,
  useColorModeValue,
  Icon,
  Badge,
  Divider,
  Wrap,
  WrapItem,
  Checkbox,
  CheckboxGroup,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem
} from '@chakra-ui/react';
import {
  MdSearch,
  MdRefresh,
  MdBarChart,
  MdAttachMoney,
  MdDownload
} from 'react-icons/md';
import { getToken } from '../../../../services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL
  : process.env.REACT_APP_LOCAL_API_URL;

// All Brazilian states organized by region
const STATES_BY_REGION = {
  'Norte': ['AC', 'AP', 'AM', 'PA', 'RO', 'RR', 'TO'],
  'Nordeste': ['AL', 'BA', 'CE', 'MA', 'PB', 'PE', 'PI', 'RN', 'SE'],
  'Centro-Oeste': ['DF', 'GO', 'MT', 'MS'],
  'Sudeste': ['ES', 'MG', 'RJ', 'SP'],
  'Sul': ['PR', 'RS', 'SC']
};

// Principal states (most active)
const PRINCIPAL_STATES = ['BA', 'PB', 'MA', 'RN'];

const PrecatorioStatistics = () => {
  const [selectedStates, setSelectedStates] = useState([]);
  const [minValue, setMinValue] = useState(0);
  const [maxValue, setMaxValue] = useState(10000000); // 10 million default
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const headingColor = useColorModeValue('gray.800', 'white');
  const highlightColor = useColorModeValue('blue.500', 'blue.300');
  const statBg = useColorModeValue('gray.50', 'gray.700');

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  // Format number
  const formatNumber = (value) => {
    return new Intl.NumberFormat('pt-BR').format(value || 0);
  };

  // Export to PDF
  const exportToPDF = (language = 'pt') => {
    const translations = {
      pt: {
        reportTitle: 'Estatísticas de Precatórios por Valor',
        generationDate: 'Data de Geração',
        overallStats: 'Estatísticas Gerais',
        stateStats: 'Estatísticas por Estado',
        totalPrecatories: 'Total de Precatórios',
        totalValue: 'Valor Total',
        averageTicket: 'Ticket Médio',
        state: 'Estado',
        count: 'Quantidade',
        value: 'Valor',
        filters: 'Filtros Aplicados',
        selectedStates: 'Estados Selecionados',
        valueRange: 'Faixa de Valor',
        minValue: 'Valor Mínimo',
        maxValue: 'Valor Máximo',
        automaticDocument: 'Documento gerado automaticamente pelo sistema',
        inSelectedRange: 'No intervalo selecionado',
        sumAllValues: 'Soma de todos os valores',
        avgPerPrecatory: 'Valor médio por precatório',
        statesWithNoInfo: 'Estados sem dados'
      },
      en: {
        reportTitle: 'Precatory Statistics by Value',
        generationDate: 'Generation Date',
        overallStats: 'Overall Statistics',
        stateStats: 'Statistics by State',
        totalPrecatories: 'Total Precatories',
        totalValue: 'Total Value',
        averageTicket: 'Average Ticket',
        state: 'State',
        count: 'Count',
        value: 'Value',
        filters: 'Applied Filters',
        selectedStates: 'Selected States',
        valueRange: 'Value Range',
        minValue: 'Minimum Value',
        maxValue: 'Maximum Value',
        automaticDocument: 'Document automatically generated by the system',
        inSelectedRange: 'In selected range',
        sumAllValues: 'Sum of all values',
        avgPerPrecatory: 'Average value per precatory',
        statesWithNoInfo: 'States with no info'
      }
    };

    const t = translations[language] || translations.pt;

    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    // Header with colored background
    doc.setFillColor(52, 152, 219);
    doc.rect(0, 0, pageWidth, 18, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text(t.reportTitle, pageWidth / 2, 10, { align: 'center' });

    const dateFormat = language === 'en' ? 'en-US' : 'pt-BR';
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`${t.generationDate}: ${new Date().toLocaleString(dateFormat)}`,
             pageWidth / 2, 14, { align: 'center' });

    doc.setTextColor(0, 0, 0);
    let yPos = 25;

    // Filters Section
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text(t.filters, 14, yPos);
    yPos += 8;

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`${t.selectedStates}: ${selectedStates.join(', ')}`, 14, yPos);
    yPos += 6;

    // Find and display states with no data
    const statesWithNoData = Object.entries(statistics.byState)
      .filter(([, stats]) => stats.count === 0)
      .map(([state]) => state);

    if (statesWithNoData.length > 0) {
      doc.text(`${t.statesWithNoInfo}: ${statesWithNoData.join(', ')}`, 14, yPos);
      yPos += 6;
    }

    doc.text(`${t.valueRange}: ${formatCurrency(minValue)} - ${formatCurrency(maxValue)}`,
             14, yPos);
    yPos += 12;

    // Overall Statistics Section
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text(t.overallStats, 14, yPos);
    doc.setLineWidth(0.5);
    doc.line(14, yPos + 2, pageWidth - 14, yPos + 2);
    yPos += 10;

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');

    // Total Precatories Box
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.rect(14, yPos - 5, pageWidth - 28, 10);
    doc.text(`${t.totalPrecatories}:`, 16, yPos);
    doc.setFont(undefined, 'bold');
    doc.text(formatNumber(statistics.overall.count), pageWidth - 16, yPos,
             { align: 'right' });
    doc.setFont(undefined, 'normal');
    doc.setFontSize(8);
    doc.text(t.inSelectedRange, 16, yPos + 4);
    doc.setFontSize(10);
    yPos += 12;

    // Total Value Box
    doc.rect(14, yPos - 5, pageWidth - 28, 10);
    doc.text(`${t.totalValue}:`, 16, yPos);
    doc.setFont(undefined, 'bold');
    doc.text(formatCurrency(statistics.overall.totalValue), pageWidth - 16, yPos,
             { align: 'right' });
    doc.setFont(undefined, 'normal');
    doc.setFontSize(8);
    doc.text(t.sumAllValues, 16, yPos + 4);
    doc.setFontSize(10);
    yPos += 12;

    // Average Ticket Box
    doc.rect(14, yPos - 5, pageWidth - 28, 10);
    doc.text(`${t.averageTicket}:`, 16, yPos);
    doc.setFont(undefined, 'bold');
    doc.text(formatCurrency(statistics.overall.averageTicket), pageWidth - 16, yPos,
             { align: 'right' });
    doc.setFont(undefined, 'normal');
    doc.setFontSize(8);
    doc.text(t.avgPerPrecatory, 16, yPos + 4);
    doc.setFontSize(10);
    yPos += 18;

    // State Statistics Table
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text(t.stateStats, 14, yPos);
    yPos += 2;

    const tableColumns = [t.state, t.count, t.totalValue, t.averageTicket];
    const tableRows = [];

    Object.entries(statistics.byState)
      .sort(([, a], [, b]) => b.totalValue - a.totalValue)
      .forEach(([state, stats]) => {
        if (stats.count > 0) {
          tableRows.push([
            state,
            formatNumber(stats.count),
            formatCurrency(stats.totalValue),
            formatCurrency(stats.averageTicket)
          ]);
        }
      });

    autoTable(doc, {
      head: [tableColumns],
      body: tableRows,
      startY: yPos,
      styles: {
        fontSize: 9,
        cellPadding: 4,
        lineColor: [200, 200, 200],
        lineWidth: 0.3
      },
      headStyles: {
        fillColor: [52, 152, 219],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center'
      },
      columnStyles: {
        0: { halign: 'center' },
        1: { halign: 'center' },
        2: { halign: 'right' },
        3: { halign: 'right' }
      },
      margin: { left: 14, right: 14 }
    });

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(t.automaticDocument, pageWidth / 2, pageHeight - 10,
             { align: 'center' });

    // Save with language-specific filename
    const filePrefix = language === 'en' ?
      'Precatory_Statistics' : 'Estatisticas_Precatorios';
    doc.save(`${filePrefix}_${new Date().toISOString().split('T')[0]}.pdf`);

    toast({
      title: language === 'pt' ? 'PDF exportado com sucesso' : 'PDF exported successfully',
      status: 'success',
      duration: 3000,
      isClosable: true
    });
  };

  // Select all states
  const handleSelectAll = () => {
    const allStates = Object.values(STATES_BY_REGION).flat();
    setSelectedStates(allStates);
  };

  // Select principal states
  const handleSelectPrincipal = () => {
    setSelectedStates(PRINCIPAL_STATES);
  };

  // Clear selection
  const handleClearSelection = () => {
    setSelectedStates([]);
  };

  // Handle state checkbox change
  const handleStateChange = (states) => {
    setSelectedStates(states);
  };

  // Fetch statistics
  const handleFetchStatistics = async () => {
    // Validation
    if (selectedStates.length === 0) {
      toast({
        title: 'Selecione pelo menos um estado',
        status: 'warning',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    if (minValue < 0 || maxValue < 0) {
      toast({
        title: 'Os valores devem ser positivos',
        status: 'warning',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    if (minValue > maxValue) {
      toast({
        title: 'O valor mínimo não pode ser maior que o máximo',
        status: 'warning',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    setLoading(true);

    try {
      const token = getToken();

      const response = await fetch(`${API_BASE_URL}/precatorios/statistics/value-range`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          states: selectedStates,
          minValue,
          maxValue
        })
      });

      const data = await response.json();

      if (data.success) {
        setStatistics(data.data);
        toast({
          title: 'Estatísticas carregadas com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true
        });
      } else {
        throw new Error(data.message || 'Erro ao carregar estatísticas');
      }
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      toast({
        title: 'Erro ao carregar estatísticas',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  };

  // Clear filters
  const handleClearFilters = () => {
    setSelectedStates([]);
    setMinValue(0);
    setMaxValue(10000000);
    setStatistics(null);
  };

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between">
          <Box>
            <Heading size="lg" color={headingColor}>
              <Icon as={MdBarChart} mr={2} />
              Estatísticas por Valor
            </Heading>
            <Text color={textColor} mt={1}>
              Analise precatórios por faixa de valor
            </Text>
          </Box>
        </HStack>

        {/* Filters Card */}
        <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
          <CardHeader>
            <Heading size="md" color={headingColor}>
              Filtros
            </Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={6} align="stretch">
              {/* State Selection */}
              <Box>
                <Text fontWeight="bold" mb={3} color={headingColor}>
                  Selecionar Estados
                </Text>
                <HStack spacing={3} mb={4}>
                  <Button
                    size="sm"
                    onClick={handleSelectAll}
                    colorScheme="blue"
                    variant="outline"
                  >
                    Todos os Estados ({Object.values(STATES_BY_REGION).flat().length})
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSelectPrincipal}
                    colorScheme="green"
                    variant="outline"
                  >
                    Principais (BA, PB, MA, RN)
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleClearSelection}
                    variant="ghost"
                  >
                    Limpar
                  </Button>
                </HStack>

                {/* States by Region Accordion */}
                <Accordion allowMultiple>
                  {Object.entries(STATES_BY_REGION).map(([region, states]) => (
                    <AccordionItem key={region}>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            {region} ({states.length})
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                      </h2>
                      <AccordionPanel pb={4}>
                        <CheckboxGroup value={selectedStates} onChange={handleStateChange}>
                          <Wrap spacing={4}>
                            {states.map(state => (
                              <WrapItem key={state}>
                                <Checkbox value={state}>
                                  <Badge colorScheme={selectedStates.includes(state) ? 'blue' : 'gray'}>
                                    {state}
                                  </Badge>
                                </Checkbox>
                              </WrapItem>
                            ))}
                          </Wrap>
                        </CheckboxGroup>
                      </AccordionPanel>
                    </AccordionItem>
                  ))}
                </Accordion>

                {selectedStates.length > 0 && (
                  <Text mt={3} color={highlightColor} fontWeight="bold">
                    {selectedStates.length} estado(s) selecionado(s): {selectedStates.join(', ')}
                  </Text>
                )}
              </Box>

              <Divider />

              {/* Value Range */}
              <Box>
                <Text fontWeight="bold" mb={3} color={headingColor}>
                  Faixa de Valor
                </Text>
                <HStack spacing={4}>
                  <Box flex={1}>
                    <Text fontSize="sm" mb={2} color={textColor}>
                      Valor Mínimo
                    </Text>
                    <NumberInput
                      value={minValue}
                      onChange={(valueString, valueNumber) => setMinValue(valueNumber)}
                      min={0}
                      step={10000}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    <Text fontSize="xs" color={textColor} mt={1}>
                      {formatCurrency(minValue)}
                    </Text>
                  </Box>
                  <Box flex={1}>
                    <Text fontSize="sm" mb={2} color={textColor}>
                      Valor Máximo
                    </Text>
                    <NumberInput
                      value={maxValue}
                      onChange={(valueString, valueNumber) => setMaxValue(valueNumber)}
                      min={0}
                      step={10000}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    <Text fontSize="xs" color={textColor} mt={1}>
                      {formatCurrency(maxValue)}
                    </Text>
                  </Box>
                </HStack>
              </Box>

              {/* Action Buttons */}
              <HStack spacing={3}>
                <Button
                  leftIcon={<Icon as={MdSearch} />}
                  colorScheme="blue"
                  onClick={handleFetchStatistics}
                  isLoading={loading}
                  loadingText="Buscando..."
                >
                  Buscar Estatísticas
                </Button>
                <Button
                  leftIcon={<Icon as={MdRefresh} />}
                  variant="outline"
                  onClick={handleClearFilters}
                >
                  Limpar Filtros
                </Button>
                {statistics && (
                  <Menu>
                    <MenuButton
                      as={Button}
                      leftIcon={<Icon as={MdDownload} />}
                      colorScheme="red"
                      rightIcon={<span>▼</span>}
                    >
                      Exportar PDF
                    </MenuButton>
                    <MenuList>
                      <MenuItem onClick={() => exportToPDF('pt')}>
                        🇧🇷 Português
                      </MenuItem>
                      <MenuItem onClick={() => exportToPDF('en')}>
                        🇺🇸 English
                      </MenuItem>
                    </MenuList>
                  </Menu>
                )}
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Statistics Display */}
        {loading && (
          <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
            <CardBody textAlign="center" py={12}>
              <Spinner size="xl" color={highlightColor} thickness="4px" />
              <Text mt={4} color={textColor}>Carregando estatísticas...</Text>
            </CardBody>
          </Card>
        )}

        {!loading && statistics && (
          <>
            {/* Overall Statistics */}
            <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
              <CardHeader>
                <Heading size="md" color={headingColor}>
                  <Icon as={MdAttachMoney} mr={2} />
                  Resumo Geral
                </Heading>
              </CardHeader>
              <CardBody>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                  <Stat bg={statBg} p={4} borderRadius="md">
                    <StatLabel>Total de Precatórios</StatLabel>
                    <StatNumber>{formatNumber(statistics.overall.count)}</StatNumber>
                    <StatHelpText>No intervalo selecionado</StatHelpText>
                  </Stat>
                  <Stat bg={statBg} p={4} borderRadius="md">
                    <StatLabel>Valor Total</StatLabel>
                    <StatNumber>{formatCurrency(statistics.overall.totalValue)}</StatNumber>
                    <StatHelpText>Soma de todos os valores</StatHelpText>
                  </Stat>
                  <Stat bg={statBg} p={4} borderRadius="md">
                    <StatLabel>Ticket Médio</StatLabel>
                    <StatNumber>{formatCurrency(statistics.overall.averageTicket)}</StatNumber>
                    <StatHelpText>Valor médio por precatório</StatHelpText>
                  </Stat>
                </SimpleGrid>
              </CardBody>
            </Card>

            {/* By State Statistics */}
            <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
              <CardHeader>
                <Heading size="md" color={headingColor}>
                  Estatísticas por Estado
                </Heading>
              </CardHeader>
              <CardBody>
                {Object.keys(statistics.byState).length === 0 ? (
                  <Alert status="info">
                    <AlertIcon />
                    Nenhum precatório encontrado no intervalo selecionado
                  </Alert>
                ) : (
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                    {Object.entries(statistics.byState)
                      .sort(([, a], [, b]) => b.totalValue - a.totalValue)
                      .map(([state, stats]) => (
                        <Card
                          key={state}
                          variant="outline"
                          size="sm"
                          borderColor={stats.count > 0 ? highlightColor : borderColor}
                        >
                          <CardBody>
                            {stats.count === 0 ? (
                              <VStack spacing={3} py={4}>
                                <Badge colorScheme="gray" fontSize="md">
                                  {state}
                                </Badge>
                                <Icon as={MdBarChart} w={8} h={8} color={textColor} />
                                <Text fontSize="sm" color={textColor} textAlign="center">
                                  Não temos dados deste Estado, no momento
                                </Text>
                              </VStack>
                            ) : (
                              <VStack align="stretch" spacing={2}>
                                <HStack justify="space-between">
                                  <Badge colorScheme="blue" fontSize="md">
                                    {state}
                                  </Badge>
                                  <Text fontWeight="bold" color={textColor}>
                                    {formatNumber(stats.count)} prec.
                                  </Text>
                                </HStack>
                                <Divider />
                                <Box>
                                  <Text fontSize="xs" color={textColor}>
                                    Valor Total
                                  </Text>
                                  <Text fontWeight="bold" color={headingColor}>
                                    {formatCurrency(stats.totalValue)}
                                  </Text>
                                </Box>
                                <Box>
                                  <Text fontSize="xs" color={textColor}>
                                    Ticket Médio
                                  </Text>
                                  <Text fontWeight="bold" color={highlightColor}>
                                    {formatCurrency(stats.averageTicket)}
                                  </Text>
                                </Box>
                              </VStack>
                            )}
                          </CardBody>
                        </Card>
                      ))}
                  </SimpleGrid>
                )}
              </CardBody>
            </Card>
          </>
        )}

        {!loading && !statistics && (
          <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
            <CardBody textAlign="center" py={12}>
              <Icon as={MdBarChart} w={16} h={16} color={textColor} mb={4} />
              <Text color={textColor}>
                Selecione os filtros e clique em "Buscar Estatísticas" para ver os resultados
              </Text>
            </CardBody>
          </Card>
        )}
      </VStack>
    </Box>
  );
};

export default PrecatorioStatistics;
